// src/modules/components/reports/calls/CallReports.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import { Title } from "react-admin";

// Import your created components
import SummaryCards from "./SummaryCards";
import CallReportsFilters from "./CallReportsFilters";
import CallReportsCharts from "./CallReportsCharts";
import CallReportsDatagrid from "./CallReportsDatagrid";

const CallReports = () => {
  const theme = useTheme();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Filter states - consolidated in one object
  const [filters, setFilters] = useState({
    timeRange: "",
    callType: "",
    disposition: "",
    agentId: "",
    startDate: "",
    endDate: "",
  });

  // Fetch data when filters change
  useEffect(() => {
    const fetchCallReportData = async () => {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const params = new URLSearchParams();
        
        // Add all non-empty filters to params
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });

        const url =
          `${process.env.REACT_APP_URL}/admin/reports/calls` +
          (params.toString() ? `?${params.toString()}` : "");
        
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch call report data");

        const result = await response.json();
        setReportData(result.data || {});
      } catch (err) {
        console.error("Error fetching call report:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCallReportData();
  }, [filters]);

  // Handler for filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load call report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error}
        </Box>
      </Alert>
    );
  }

  // No data state
  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No call report data available.
      </Alert>
    );
  }

  const { summary, charts } = reportData;

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title="Call Reports" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Call Analytics Dashboard
        </Typography>
      </Box>

      {/* Summary Cards */}
      <SummaryCards summary={summary} />

      {/* Filters */}
      <CallReportsFilters 
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Tabs Navigation */}
      <Box sx={{ mb: 3 }}>
        <Paper elevation={2}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Analytics Overview" />
            <Tab label="All Call Records" />
          </Tabs>
        </Paper>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 ? (
        // Analytics Overview Tab
        <CallReportsCharts 
          charts={charts}
          theme={theme}
        />
      ) : (
        // All Call Records Tab
        <CallReportsDatagrid filter={filters}/>
      )}
    </Box>
  );
};

export default CallReports;